import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, CircularProgress, Typography, useTheme } from '@mui/material'
import { useTenantBranding } from '@/hooks/use-tenant-branding'
import { primaryActionSx } from '@/theme/primary-action'
import { tenantActionCtaSx } from '@/theme/tenant-cta-sx'

type CartItem = {
  id: string | number
  name: string
  price?: number
  item_price?: number
  quantity?: number
  count?: number
}

type CartFooterProps = {
  cartItems?: CartItem[]
  onViewCart: () => void | Promise<void>
  summary?: {
    totalQuantity: number
    totalPrice: number
  }
  dockAboveMobileNav?: boolean
  inBottomStack?: boolean
}

/**
 * Hook-ready summary helper so screens can either pass raw cartItems
 * or provide global-store derived totals via `summary`.
 */
export function useCartFooterSummary(cartItems: CartItem[]): { totalQuantity: number; totalPrice: number } {
  return useMemo(
    () =>
      cartItems.reduce(
        (acc, item) => {
          const rawQuantity = item.quantity ?? item.count
          const parsedQuantity = Number(rawQuantity)
          const quantity = Number.isFinite(parsedQuantity) ? Math.max(0, parsedQuantity) : 1
          const price = Number(item.price ?? item.item_price) || 0
          acc.totalQuantity += quantity
          acc.totalPrice += quantity * price
          return acc
        },
        { totalQuantity: 0, totalPrice: 0 }
      ),
    [cartItems]
  )
}

function CartFooter({
  cartItems = [],
  onViewCart,
  summary,
  dockAboveMobileNav = false,
  inBottomStack = false,
}: CartFooterProps): React.ReactElement {
  const theme = useTheme()
  const { primaryTokens } = useTenantBranding()
  const ctaOverlaySx = primaryTokens ? tenantActionCtaSx(primaryTokens) : primaryActionSx
  const [shouldBounce, setShouldBounce] = useState(false)
  const [shouldPulse, setShouldPulse] = useState(false)
  const [isNavigating, setIsNavigating] = useState(false)
  const prevTotalsRef = useRef({ totalQuantity: 0, totalPrice: 0 })
  const navUnlockTimerRef = useRef<number | null>(null)
  const calculatedSummary = useCartFooterSummary(cartItems)

  const totalQuantity = Math.max(0, Number(summary?.totalQuantity ?? calculatedSummary.totalQuantity) || 0)
  const totalPrice = Math.max(0, Number(summary?.totalPrice ?? calculatedSummary.totalPrice) || 0)

  const isVisible = totalQuantity > 0
  const countLabel = totalQuantity === 1 ? '1 item in cart' : `${totalQuantity} items in cart`
  const liveSummary = countLabel

  useEffect(() => {
    if (!isVisible) return
    setShouldBounce(true)
    const timeoutId = window.setTimeout(() => setShouldBounce(false), 340)
    return () => window.clearTimeout(timeoutId)
  }, [isVisible])

  useEffect(() => {
    const prev = prevTotalsRef.current
    const becameVisible = prev.totalQuantity <= 0 && totalQuantity > 0
    const changedWhileVisible =
      totalQuantity > 0 &&
      (prev.totalQuantity !== totalQuantity || Math.round(prev.totalPrice) !== Math.round(totalPrice))

    if (!becameVisible && changedWhileVisible) {
      setShouldPulse(true)
      const pulseId = window.setTimeout(() => setShouldPulse(false), 220)
      prevTotalsRef.current = { totalQuantity, totalPrice }
      return () => window.clearTimeout(pulseId)
    }
    prevTotalsRef.current = { totalQuantity, totalPrice }
  }, [totalQuantity, totalPrice])

  useEffect(() => {
    return () => {
      if (navUnlockTimerRef.current != null) {
        window.clearTimeout(navUnlockTimerRef.current)
      }
    }
  }, [])

  const handleViewCart = useCallback(async () => {
    if (isNavigating || !isVisible) return
    setIsNavigating(true)
    try {
      await onViewCart()
    } finally {
      // Keep lock briefly so rapid taps on slow devices cannot double-trigger navigation.
      if (navUnlockTimerRef.current != null) {
        window.clearTimeout(navUnlockTimerRef.current)
      }
      navUnlockTimerRef.current = window.setTimeout(() => setIsNavigating(false), 220)
    }
  }, [isNavigating, isVisible, onViewCart])

  const ctaButton = (
    <Box
      component="button"
      onClick={handleViewCart}
      disabled={isNavigating || !isVisible}
      type="button"
      aria-label={isNavigating ? `Opening cart, ${liveSummary}` : `View cart, ${liveSummary}`}
      data-testid="cart-footer-view-cart"
      data-tenant-primary={primaryTokens?.fill}
      sx={[
        {
          pointerEvents: 'auto',
          width: '100%',
          height: 56,
          minHeight: 56,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          border: 0,
          cursor: isNavigating || !isVisible ? 'not-allowed' : 'pointer',
          borderRadius: '19px',
          px: '17px',
          /* Soft lift so a tighter gap above the bottom nav still reads as intentional */
          boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.06), 0 4px 14px rgba(0, 0, 0, 0.18)',
          transform: isNavigating ? 'scale(0.995)' : 'scale(1)',
          opacity: isNavigating ? 0.9 : 1,
          transition: 'transform 150ms ease, opacity 150ms ease, background-color 200ms ease, filter 200ms ease',
          ...(shouldBounce && {
            animation: 'cartFooterBounce 340ms cubic-bezier(0.22, 1, 0.36, 1)',
          }),
          ...(shouldPulse && {
            animation: 'cartFooterPulse 220ms ease-out',
          }),
          '&:active': {
            transform: 'scale(0.985)',
          },
          '&:disabled': primaryActionSx['&.Mui-disabled'],
          '@keyframes cartFooterBounce': {
            '0%': { transform: 'translateY(0) scale(1)' },
            '42%': { transform: 'translateY(-3px) scale(1.01)' },
            '100%': { transform: 'translateY(0) scale(1)' },
          },
          '@keyframes cartFooterPulse': {
            '0%': { transform: 'scale(1)' },
            '50%': { transform: 'scale(1.012)' },
            '100%': { transform: 'scale(1)' },
          },
        },
        ctaOverlaySx,
        {
          '&:active': {
            transform: isNavigating ? 'scale(0.995)' : 'scale(0.985)',
          },
        },
      ]}
    >
      <Typography
        sx={{
          minWidth: 0,
          flex: 1,
          fontSize: theme.typography.pxToRem(14.5),
          fontWeight: 600,
          lineHeight: 1.3,
          color: primaryTokens
            ? primaryTokens.foreground
            : (t) => t.customTokens.cta.text,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}
      >
        {countLabel}
      </Typography>

      <Box
        component="span"
        data-testid="cart-footer-view-cart-chip"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          height: 42,
          minHeight: 42,
          px: '16px',
          borderRadius: '15px',
          fontSize: theme.typography.pxToRem(14),
          fontWeight: 700,
          letterSpacing: 0.1,
          color: primaryTokens
            ? primaryTokens.foreground
            : (t) => t.customTokens.cta.text,
          backgroundColor: primaryTokens
            ? primaryTokens.fillActive
            : (t) => t.customTokens.cta.fillActive,
          whiteSpace: 'nowrap',
          gap: 0.75,
        }}
      >
        <Typography
          component="span"
          sx={{ fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit', lineHeight: 1 }}
        >
          {isNavigating ? 'Opening…' : 'View Cart'}
        </Typography>
        {isNavigating && <CircularProgress size={14} sx={{ color: 'inherit' }} />}
      </Box>
    </Box>
  )

  if (inBottomStack) {
    // Mobile BottomStack: tight gap between cart CTA and bottom nav (~2px).
    // Safe area is owned by BottomNavBar — do not double-count it here.
    const bottomPad = dockAboveMobileNav
      ? 2
      : 'calc(2px + env(safe-area-inset-bottom, 0px))'
    const openMaxHeight = dockAboveMobileNav
      ? 58
      : 'calc(56px + 2px + env(safe-area-inset-bottom, 0px))'

    return (
      <Box
        aria-hidden={!isVisible}
        data-layer="cart-footer"
        sx={{
          width: '100%',
          maxHeight: isVisible ? openMaxHeight : 0,
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(100%)',
          overflow: 'hidden',
          transition: 'max-height 300ms ease-out, transform 300ms ease-out, opacity 300ms ease-out',
          px: 1.5,
          pt: 0,
          pb: isVisible ? bottomPad : 0,
          mb: 0,
          pointerEvents: isVisible ? 'auto' : 'none',
        }}
      >
        <Typography
          component="span"
          aria-live="polite"
          sx={{
            position: 'absolute',
            width: 1,
            height: 1,
            p: 0,
            m: -1,
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          {isVisible ? liveSummary : 'Cart is empty'}
        </Typography>
        {ctaButton}
      </Box>
    )
  }

  return (
    <>
      {/* Reserve space so fixed CTA never overlaps page content. */}
      <Box
        aria-hidden="true"
        sx={{
          height: isVisible
            ? dockAboveMobileNav
              ? 'calc(56px + 2px + 64px + max(env(safe-area-inset-bottom, 0px), 8px) + 8px)'
              : 'calc(56px + 2px + env(safe-area-inset-bottom, 0px) + 8px)'
            : 0,
          transition: 'height 300ms ease-out',
          // Mobile-only reserve; md+ layout does not use the docked offset.
          display: { md: 'none' },
        }}
      />
      <Box
        aria-hidden="true"
        sx={{
          display: { xs: 'none', md: 'block' },
          height: isVisible ? 'calc(56px + 12px + env(safe-area-inset-bottom, 0px) + 8px)' : 0,
          transition: 'height 300ms ease-out',
        }}
      />

      <Box
        aria-hidden={!isVisible}
        data-layer="cart-footer"
        sx={{
          pointerEvents: 'none',
          position: 'fixed',
          left: 0,
          right: 0,
          // Mobile: sit ~2px above the 64px nav pill (+ nav safe-area padding).
          // md+: flush to bottom (no mobile nav).
          bottom: {
            xs: dockAboveMobileNav
              ? 'calc(64px + 2px + max(env(safe-area-inset-bottom, 0px), 8px))'
              : 0,
            md: 0,
          },
          zIndex: 1200,
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 300ms ease-out, opacity 300ms ease-out',
        }}
      >
        <Typography
          component="span"
          aria-live="polite"
          sx={{
            position: 'absolute',
            width: 1,
            height: 1,
            p: 0,
            m: -1,
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          {isVisible ? liveSummary : 'Cart is empty'}
        </Typography>

        <Box
          sx={{
            mx: 'auto',
            width: '100%',
            maxWidth: 960,
            px: { xs: 1.5, sm: 2 },
            pb: {
              xs: dockAboveMobileNav
                ? 0
                : 'calc(2px + env(safe-area-inset-bottom, 0px))',
              md: 'calc(12px + env(safe-area-inset-bottom, 0px))',
            },
          }}
        >
          {ctaButton}
        </Box>
      </Box>
    </>
  )
}

export default CartFooter
